import { useState, useEffect, useCallback, useMemo } from 'react';
import api, { flujoCajaApi, getFallbackToken } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { SearchableCombobox } from '../ui/searchable-combobox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from '../../hooks/use-toast';
import { toast as toastUI } from 'sonner';
import { FechaPagoPicker } from './FechaPagoPicker';
import { DatePickerInput } from '../ui/date-picker';
import { ArrowDownLeft, Download, Filter, Pencil, Plus, Search, Trash2, CalendarClock, AlertTriangle, CheckCircle2, BadgeCheck, RefreshCw, Mail, Copy, Phone } from 'lucide-react';

const formatCLP = (value) =>
  new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

const todayISO = () => new Date().toISOString().split('T')[0];

const estadosColors = {
  pendiente: 'bg-blue-100 text-blue-800 border-blue-200',
  pagada: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  mora: 'bg-red-100 text-red-800 border-red-200',
};

const emptyForm = () => ({
  folio: '', cliente: '', monto: 0, fecha_venc: todayISO(), estado: 'pendiente', nota: '',
});

export default function CxCPanel() {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('pendiente');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [confirmPay, setConfirmPay] = useState(null); // {cxc, montoReal}
  const [paying, setPaying] = useState(false);
  const [pagadasConError, setPagadasConError] = useState([]);
  // Iter 286.q — Cobro agrupado (batch) por cliente
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchFecha, setBatchFecha] = useState(todayISO());
  const [batchNota, setBatchNota] = useState('');
  const [batchProcessing, setBatchProcessing] = useState(false);

  // Punto 4 Carlos (2026-05-01) — Reporte Estado de Cuenta por cliente
  const [reporteDialog, setReporteDialog] = useState(false);
  const [clientesConSaldo, setClientesConSaldo] = useState([]);
  const [selClienteId, setSelClienteId] = useState('');
  const [reportePreview, setReportePreview] = useState(null); // {cliente, facturas, total_saldo...}
  const [reporteTO, setReporteTO] = useState('');
  const [reporteCC, setReporteCC] = useState('rmartinez@chirinosteel.com, c.chirino@chirinosteel.com, rchirino@chirinosteel.com');
  const [reporteSubject, setReporteSubject] = useState('');
  const [reporteMsg, setReporteMsg] = useState('');
  const [reporteSending, setReporteSending] = useState(false);
  const [reporteLoadingPreview, setReporteLoadingPreview] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const estado = filtroEstado === 'todos' ? '' : filtroEstado;
      const res = await flujoCajaApi.listCxC(estado);
      setItems(res.data.items || []);
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error al cargar CxC', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [filtroEstado]);

  // Load paid CxC with Alegra sync errors — always, regardless of current filter.
  // This powers the "⚠️ facturas con error sync" banner at the top.
  const loadErrorsBanner = useCallback(async () => {
    try {
      const res = await flujoCajaApi.listCxC('pagada');
      const conError = (res.data.items || []).filter(
        (c) => c.alegra_sync_error && !c.alegra_payment_id,
      );
      setPagadasConError(conError);
    } catch {
      setPagadasConError([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadErrorsBanner(); }, [loadErrorsBanner, items]);

  // Precargar clientes-con-saldo (con email+telefono) al montar para que el banner
  // del cliente filtrado pueda usar los botones 'Copiar resumen' y 'Llamar'.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/flujo-caja/cxc/clientes-con-saldo');
        if (!cancelled) setClientesConSaldo(res.data?.clientes || []);
      } catch { /* silencioso — el dialog reintenta al abrir */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    const qCanon = q.replace(/[.\-\s]/g, '');
    return items.filter((c) => {
      const rutRaw = (c.cliente_rut || '').toLowerCase();
      const rutCanon = rutRaw.replace(/[.\-\s]/g, '');
      return (
        (c.cliente || '').toLowerCase().includes(q) ||
        (c.folio || '').toLowerCase().includes(q) ||
        rutRaw.includes(q) ||
        (qCanon && rutCanon.includes(qCanon))
      );
    });
  }, [items, search]);

  // Iter 286.q — Cobro agrupado batch por cliente
  const selectedRows = useMemo(
    () => filtered.filter((c) => selectedIds.has(c.id) && c.estado === 'pendiente' && c.alegra_id),
    [filtered, selectedIds],
  );
  const selectedTotal = useMemo(
    () => selectedRows.reduce((a, c) => a + (c.monto || 0), 0),
    [selectedRows],
  );
  const selectedClientes = useMemo(
    () => new Set(selectedRows.map((c) => (c.cliente_rut || c.cliente || '').trim().toLowerCase())),
    [selectedRows],
  );
  const selectedMismoCliente = selectedClientes.size <= 1;
  const selectedClienteNombre = selectedRows[0]?.cliente || '';
  const selectedClienteRut = selectedRows[0]?.cliente_rut || '';

  const toggleSel = (id) => setSelectedIds((prev) => {
    const s = new Set(prev);
    if (s.has(id)) s.delete(id); else s.add(id);
    return s;
  });
  const selectAllVisible = () => {
    const s = new Set(selectedIds);
    filtered.forEach((c) => { if (c.estado === 'pendiente' && c.alegra_id) s.add(c.id); });
    setSelectedIds(s);
  };
  const clearSel = () => setSelectedIds(new Set());

  const abrirBatchDialog = () => {
    if (!selectedRows.length) { toastUI.warning('Selecciona al menos una factura'); return; }
    if (!selectedMismoCliente) { toastUI.error('Todas las facturas deben ser del mismo cliente'); return; }
    setBatchFecha(todayISO());
    setBatchNota('');
    setShowBatchDialog(true);
  };

  const confirmarBatchCobro = async () => {
    if (batchProcessing) return;
    setBatchProcessing(true);
    try {
      const res = await api.post('/flujo-caja/cxc/marcar-pagadas-batch', {
        cxc_ids: selectedRows.map((c) => c.id),
        fecha_pago: batchFecha,
        nota: batchNota || undefined,
      });
      toastUI.success(
        `✓ Pago agrupado creado en Alegra (#${res.data.alegra_payment_id}) por ${formatCLP(res.data.total)} · ${res.data.facturas} factura(s)`,
        { duration: 8000 },
      );
      setShowBatchDialog(false);
      clearSel();
      await load();
      await loadErrorsBanner();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || 'Error desconocido';
      toastUI.error(`No se pudo registrar el cobro agrupado: ${detail}`, { duration: 12000 });
    } finally {
      setBatchProcessing(false);
    }
  };

  const today = todayISO();
  const in91 = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 91);
    return d.toISOString().split('T')[0];
  }, []);

  const totalPendiente = useMemo(() => items.filter(c => c.estado === 'pendiente').reduce((a, c) => a + c.monto, 0), [items]);
  const totalVencido = useMemo(() =>
    items.filter(c => c.estado === 'pendiente' && c.fecha_venc < today).reduce((a, c) => a + c.monto, 0),
    [items, today]);
  const totalProximos91 = useMemo(() =>
    items.filter(c => c.estado === 'pendiente' && c.fecha_venc >= today && c.fecha_venc <= in91).reduce((a, c) => a + c.monto, 0),
    [items, today, in91]);

  // Punto 4 — Subtotal del cliente filtrado (cuando search apunta a 1 solo cliente).
  const filtroClienteResumen = useMemo(() => {
    if (!search || filtered.length === 0) return null;
    // Detectar si todas las filas filtradas son del mismo cliente (case-insensitive)
    const clientesUnicos = new Set(filtered.map(c => (c.cliente || '').trim().toLowerCase()));
    if (clientesUnicos.size !== 1) return null;
    const cliente = filtered[0].cliente;
    const pend = filtered.filter(c => c.estado === 'pendiente');
    const venc = pend.filter(c => c.fecha_venc < today);
    const totalPend = pend.reduce((a, c) => a + c.monto, 0);
    const totalVenc = venc.reduce((a, c) => a + c.monto, 0);
    // Buscar contacto (email/telefono) en clientesConSaldo precargado
    const contacto = clientesConSaldo.find(
      (x) => (x.cliente_nombre || '').trim().toLowerCase() === cliente.trim().toLowerCase(),
    ) || null;
    return {
      cliente,
      facturas: filtered.length,
      pendientes: pend.length,
      vencidas: venc.length,
      totalPend,
      totalVenc,
      email: contacto?.email || '',
      telefono: contacto?.telefono || '',
    };
  }, [filtered, search, today, clientesConSaldo]);

  // Cache de preview enriquecido (con OC/HES) por cliente_id, llenado cuando aparece banner
  const [previewEnriquecido, setPreviewEnriquecido] = useState(null); // {cliente_id, facturas}

  // Cuando el banner del cliente filtrado aparece, fetcheamos el preview enriquecido
  // (incluye OC + HES desde ventas/proformas_venta) para usarlo en el botón Copiar.
  useEffect(() => {
    if (!filtroClienteResumen) {
      setPreviewEnriquecido(null);
      return;
    }
    const contacto = clientesConSaldo.find(
      (x) => (x.cliente_nombre || '').trim().toLowerCase() === filtroClienteResumen.cliente.trim().toLowerCase(),
    );
    if (!contacto?.cliente_id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/flujo-caja/cxc/reporte-cliente/${contacto.cliente_id}/preview`);
        if (!cancelled) {
          setPreviewEnriquecido({ cliente_id: contacto.cliente_id, facturas: res.data?.facturas || [] });
        }
      } catch { /* silencioso — botón usará datos locales como fallback */ }
    })();
    return () => { cancelled = true; };
  }, [filtroClienteResumen, clientesConSaldo]);

  // Copiar resumen al portapapeles con fallback execCommand para iframes/preview
  const copiarResumenCliente = async () => {
    if (!filtroClienteResumen) return;
    const r = filtroClienteResumen;
    const lineas = [
      `Estado de Cuenta — ${r.cliente}`,
      `Total adeudado: ${formatCLP(r.totalPend)}`,
      `Facturas pendientes: ${r.pendientes}`,
    ];
    if (r.vencidas > 0) lineas.push(`Vencidas: ${r.vencidas} (${formatCLP(r.totalVenc)})`);

    // Usar facturas enriquecidas (con OC + HES) si el preview ya cargó; sino, fallback a items locales
    const facsEnriq = previewEnriquecido?.facturas || [];
    const useEnriq = facsEnriq.length > 0;
    const detalle = useEnriq
      ? facsEnriq.slice(0, 12)
      : filtered.filter((c) => c.estado === 'pendiente').slice(0, 12);

    if (detalle.length > 0) {
      lineas.push('');
      lineas.push('Detalle:');
      for (const f of detalle) {
        if (useEnriq) {
          const oc = f.orden_compra ? ` · OC: ${f.orden_compra}` : '';
          const hes = f.numero_hes ? ` · HES: ${f.numero_hes}` : '';
          lineas.push(`• ${f.folio} — ${formatCLP(f.saldo)} — vence ${f.fecha_vencimiento}${oc}${hes}`);
        } else {
          lineas.push(`• ${f.folio} — ${formatCLP(f.monto)} — vence ${f.fecha_venc}`);
        }
      }
      const totalFac = useEnriq ? facsEnriq.length : r.pendientes;
      if (totalFac > 12) lineas.push(`… y ${totalFac - 12} más`);
    }
    const texto = lineas.join('\n');

    // 1) Intentar Clipboard API moderna (síncrono)
    let copied = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(texto);
        copied = true;
      }
    } catch { /* ignored, fallback abajo */ }

    // 2) Fallback robusto: textarea oculto + execCommand (funciona en iframes/preview)
    if (!copied) {
      try {
        const ta = document.createElement('textarea');
        ta.value = texto;
        ta.style.position = 'fixed';
        ta.style.top = '0';
        ta.style.left = '0';
        ta.style.opacity = '0';
        ta.setAttribute('readonly', '');
        document.body.appendChild(ta);
        ta.select();
        ta.setSelectionRange(0, texto.length);
        const ok = document.execCommand && document.execCommand('copy');
        document.body.removeChild(ta);
        copied = !!ok;
      } catch { copied = false; }
    }

    if (copied) {
      toast({ title: 'Resumen copiado', description: 'Pegalo en WhatsApp/Email/etc.' });
    } else {
      toast({
        title: 'No se pudo copiar',
        description: 'Tu navegador bloqueó el portapapeles. Selecciona el texto manualmente.',
        variant: 'destructive',
      });
    }
  };

  const openNew = () => { setForm(emptyForm()); setEditing('new'); };
  const openEdit = (c) => {
    setForm({
      folio: c.folio, cliente: c.cliente, monto: c.monto,
      fecha_venc: c.fecha_venc, estado: c.estado, nota: c.nota || '',
      fecha_venc_original: c.fecha_venc_original,
    });
    setEditing(c);
  };
  const close = () => { setEditing(null); setForm(emptyForm()); };

  const save = async () => {
    if (!form.folio.trim() || !form.cliente.trim()) {
      toast({ title: 'Folio y cliente son requeridos', variant: 'destructive' }); return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await flujoCajaApi.createCxC(form);
        toast({ title: 'CxC creada' });
      } else {
        await flujoCajaApi.updateCxC(editing.id, form);
        toast({ title: 'CxC actualizada' });
      }
      close(); load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const doDelete = async (c) => {
    if (!window.confirm(`¿Eliminar factura ${c.folio} (${c.cliente})?`)) return;
    try {
      await flujoCajaApi.deleteCxC(c.id);
      toast({ title: 'Eliminada' }); load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const importAlegra = async () => {
    if (!window.confirm('Importar CxC pendientes desde Alegra? Las fechas editadas manualmente se preservan.')) return;
    try {
      const res = await flujoCajaApi.importCxCAlegra();
      toast({ title: `Importadas: ${res.data.inserted} nuevas, ${res.data.updated} actualizadas` });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const changeFechaInline = async (c, newFecha) => {
    if (!newFecha || newFecha === c.fecha_venc) return;
    try {
      await flujoCajaApi.cambiarFechaCxC(c.id, newFecha);
      toast({ title: `Fecha actualizada: ${c.folio} → ${newFecha}` });
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const marcarPagada = async (c) => {
    // Abre el AlertDialog en-app (window.confirm queda oculto bajo Dialog Radix)
    setConfirmPay({ cxc: c });
  };

  const doMarcarPagada = async () => {
    if (!confirmPay?.cxc) return;
    const c = confirmPay.cxc;
    setPaying(true);
    try {
      const res = await flujoCajaApi.marcarPagadaCxC(c.id);
      const alegra = res.data?.alegra;
      if (alegra?.synced) {
        toast({
          title: 'Pagada · sincronizada con Alegra',
          description: `Pago #${alegra.payment_id} en ${alegra.account} (${alegra.method}) por ${formatCLP(res.data?.monto_pagado_alegra || 0)}`,
        });
      } else if (res.data?.monto_pagado_alegra === 0) {
        toast({ title: 'Pagada localmente', description: 'El total ya estaba cubierto por factoring — sin push adicional a Alegra.' });
      } else {
        toast({
          title: 'Pagada localmente · pendiente sync Alegra',
          description: alegra?.error || 'Usa el botón "reintentar" en la fila.',
          variant: 'destructive',
        });
      }
      setConfirmPay(null);
      load();
    } catch (err) {
      toast({ title: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    } finally {
      setPaying(false);
    }
  };

  const reintentarAlegra = async (c) => {
    try {
      await flujoCajaApi.reintentarAlegraCxC(c.id);
      toast({ title: `Pago sincronizado con Alegra · ${c.folio}` });
      load();
      loadErrorsBanner();
    } catch (err) {
      toast({ title: 'No se pudo sincronizar', description: err?.response?.data?.detail || 'Error', variant: 'destructive' });
    }
  };

  const reintentarTodosErrores = async () => {
    if (pagadasConError.length === 0) return;
    let ok = 0, fail = 0;
    for (const c of pagadasConError) {
      try {
        await flujoCajaApi.reintentarAlegraCxC(c.id);
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    toast({
      title: `Reintentos: ${ok} OK, ${fail} fallidos`,
      description: fail > 0 ? 'Revisá credenciales Alegra si sigue fallando' : 'Todos los pagos pusheados',
      variant: fail > 0 ? 'destructive' : 'default',
    });
    load();
    loadErrorsBanner();
  };

  // ─── Punto 4: Reporte CxC por cliente ───
  const openReporteDialog = async (preselectClienteName = null) => {
    setReporteDialog(true);
    setSelClienteId('');
    setReportePreview(null);
    try {
      const res = await api.get('/flujo-caja/cxc/clientes-con-saldo');
      const list = res.data?.clientes || [];
      setClientesConSaldo(list);
      if (preselectClienteName) {
        const target = list.find(
          (c) => (c.cliente_nombre || '').trim().toLowerCase() === preselectClienteName.trim().toLowerCase(),
        );
        if (target) {
          setSelClienteId(target.cliente_id);
          await loadReportePreview(target.cliente_id);
        }
      }
    } catch (err) {
      toast({ title: 'No se pudo cargar lista de clientes', variant: 'destructive' });
    }
  };

  const loadReportePreview = async (clienteId) => {
    if (!clienteId) {
      setReportePreview(null);
      return;
    }
    setReporteLoadingPreview(true);
    try {
      const res = await api.get(`/flujo-caja/cxc/reporte-cliente/${clienteId}/preview`);
      setReportePreview(res.data);
      setReporteTO((res.data?.default_to || []).join(', '));
      setReporteCC((res.data?.default_cc || []).join(', '));
      setReporteSubject(`Estado de Cuenta — ${res.data?.cliente?.nombre || 'Cliente'} — Chirino Steel`);
      setReporteMsg('');
    } catch (err) {
      setReportePreview(null);
      toast({ title: err?.response?.data?.detail || 'Sin facturas pendientes', variant: 'destructive' });
    } finally {
      setReporteLoadingPreview(false);
    }
  };

  const downloadReportePdf = () => {
    if (!selClienteId) return;
    const API = process.env.REACT_APP_BACKEND_URL;
    const url = `${API}/api/flujo-caja/cxc/reporte-cliente/${selClienteId}/pdf`;
    // Auth: cookie httpOnly via credentials:'include' + Bearer fallback desde sessionStorage.
    // Antes leía `localStorage.access_token` que nunca se setea (legacy/muerto).
    const token = getFallbackToken() || '';
    fetch(url, {
      credentials: 'include',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(r => r.blob())
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `estado_cuenta_${(reportePreview?.cliente?.nombre || 'cliente').replace(/\s+/g, '_')}.pdf`;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => toast({ title: 'Error descargando PDF', variant: 'destructive' }));
  };

  const enviarReporte = async () => {
    if (!selClienteId) return;
    const toList = reporteTO.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    const ccList = reporteCC.split(/[,;]/).map(e => e.trim()).filter(Boolean);
    if (toList.length === 0) {
      toast({ title: 'Falta el destinatario TO', variant: 'destructive' });
      return;
    }
    setReporteSending(true);
    try {
      await api.post(`/flujo-caja/cxc/reporte-cliente/${selClienteId}/enviar-email`, {
        to: toList, cc: ccList, subject: reporteSubject || undefined, mensaje_personal: reporteMsg || '',
      });
      toast({ title: 'Estado de Cuenta enviado', description: `A: ${toList.join(', ')}` });
      setReporteDialog(false);
    } catch (err) {
      toast({ title: 'Error enviando email', description: err?.response?.data?.detail || 'Revisá config MS Graph', variant: 'destructive' });
    } finally {
      setReporteSending(false);
    }
  };

  return (
    <Card data-testid="cxc-panel">
      <CardHeader className="pb-3">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ArrowDownLeft className="w-4 h-4 text-blue-600" /> Clientes-Ctas x Cobrar (Fila 43 del Flujo)
            </CardTitle>
            <CardDescription>
              Facturas de venta con fecha de vencimiento editable. Las pendientes dentro de 91 días se suman al grid.
            </CardDescription>
          </div>
          <div className="flex items-center gap-1.5">
            <Button size="sm" variant="outline" onClick={openReporteDialog} data-testid="btn-reporte-cxc-cliente">
              <Mail className="w-4 h-4 mr-1" /> Estado de Cuenta
            </Button>
            <Button size="sm" variant="outline" onClick={importAlegra} data-testid="btn-import-cxc-alegra">
              <Download className="w-4 h-4 mr-1" /> Importar Alegra
            </Button>
            <Button size="sm" onClick={openNew} data-testid="btn-new-cxc">
              <Plus className="w-4 h-4 mr-1" /> Nueva
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3">
          <div className="flex items-center justify-between p-2.5 rounded-md border bg-blue-50 dark:bg-blue-950/20">
            <span className="text-xs font-medium text-blue-900 dark:text-blue-200 flex items-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> Pendientes
            </span>
            <span className="text-sm font-bold tabular-nums text-blue-700" data-testid="kpi-cxc-pendientes">
              {formatCLP(totalPendiente)}
            </span>
          </div>
          <div className="flex items-center justify-between p-2.5 rounded-md border bg-red-50 dark:bg-red-950/20">
            <span className="text-xs font-medium text-red-900 dark:text-red-200 flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5" /> Vencido
            </span>
            <span className="text-sm font-bold tabular-nums text-red-700" data-testid="kpi-cxc-vencidas">
              {formatCLP(totalVencido)}
            </span>
          </div>
          <div className="flex items-center justify-between p-2.5 rounded-md border bg-emerald-50 dark:bg-emerald-950/20">
            <span className="text-xs font-medium text-emerald-900 dark:text-emerald-200 flex items-center gap-1">
              <CalendarClock className="w-3.5 h-3.5" /> Próximos 91d
            </span>
            <span className="text-sm font-bold tabular-nums text-emerald-700" data-testid="kpi-cxc-proximas">
              {formatCLP(totalProximos91)}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mt-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input
              placeholder="Buscar por cliente, folio o RUT..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 h-8 text-sm"
              data-testid="input-search-cxc"
            />
          </div>
          <div className="flex items-center gap-1">
            <Filter className="w-3.5 h-3.5 text-muted-foreground" />
            {[
              { v: 'pendiente', l: 'Pendientes' },
              { v: 'mora', l: 'En mora' },
              { v: 'pagada', l: 'Pagadas' },
              { v: 'todos', l: 'Todas' },
            ].map((o) => (
              <Button
                key={o.v}
                size="sm"
                variant={filtroEstado === o.v ? 'default' : 'outline'}
                className="text-xs h-7 px-2.5"
                onClick={() => setFiltroEstado(o.v)}
                data-testid={`filter-cxc-${o.v}`}
              >
                {o.l}
              </Button>
            ))}
          </div>
        </div>

        {selectedRows.length > 0 && (
          <div
            className="flex flex-wrap items-center gap-3 mt-2 p-2 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/20"
            data-testid="cxc-batch-toolbar"
          >
            <span className="text-xs text-emerald-900 dark:text-emerald-200 font-medium">
              {selectedRows.length} factura(s) seleccionada(s) · Total:{' '}
              <span className="tabular-nums font-bold">{formatCLP(selectedTotal)}</span>
            </span>
            {!selectedMismoCliente ? (
              <span className="text-xs text-red-700 font-medium">⚠ Selección de distintos clientes — deselecciona para agrupar</span>
            ) : (
              <span className="text-xs text-muted-foreground">
                Cliente: <b>{selectedClienteNombre}</b>{selectedClienteRut ? ` (${selectedClienteRut})` : ''}
              </span>
            )}
            <div className="ml-auto flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7"
                onClick={clearSel}
                data-testid="btn-batch-limpiar"
              >
                Limpiar
              </Button>
              <Button
                size="sm"
                className="text-xs h-7 bg-emerald-600 hover:bg-emerald-700"
                onClick={abrirBatchDialog}
                disabled={!selectedMismoCliente}
                data-testid="btn-batch-cobrar"
              >
                <BadgeCheck className="w-3.5 h-3.5 mr-1" />
                Cobrar seleccionadas (1 Ingreso en Alegra)
              </Button>
            </div>
          </div>
        )}
      </CardHeader>

      <CardContent>
        {/* Subtotal del cliente filtrado (Punto 4 Carlos) */}
        {filtroClienteResumen && (
          <div
            className="mb-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-900 p-3"
            data-testid="banner-cxc-cliente-resumen"
          >
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2 min-w-0">
                <BadgeCheck className="w-4 h-4 text-blue-600 flex-shrink-0" />
                <span className="font-semibold text-blue-900 dark:text-blue-200 truncate" title={filtroClienteResumen.cliente}>
                  {filtroClienteResumen.cliente}
                </span>
                <span className="text-xs text-blue-700 dark:text-blue-300">
                  · {filtroClienteResumen.pendientes} pendientes{filtroClienteResumen.vencidas > 0 && ` · ${filtroClienteResumen.vencidas} vencidas`}
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="text-right">
                  <div className="text-[10px] text-blue-700 dark:text-blue-300 uppercase">Total adeudado</div>
                  <div className="text-sm font-bold text-blue-900 dark:text-blue-100 tabular-nums" data-testid="cxc-cliente-total-pend">
                    {formatCLP(filtroClienteResumen.totalPend)}
                  </div>
                </div>
                {filtroClienteResumen.totalVenc > 0 && (
                  <div className="text-right border-l border-blue-300 dark:border-blue-800 pl-3">
                    <div className="text-[10px] text-red-700 dark:text-red-300 uppercase">Vencido</div>
                    <div className="text-sm font-bold text-red-700 dark:text-red-300 tabular-nums" data-testid="cxc-cliente-total-venc">
                      {formatCLP(filtroClienteResumen.totalVenc)}
                    </div>
                  </div>
                )}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={copiarResumenCliente}
                  data-testid="btn-copiar-resumen-cliente"
                  title="Copia el resumen al portapapeles para pegarlo en WhatsApp, email, etc."
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copiar
                </Button>
                {filtroClienteResumen.telefono && (
                  <a
                    href={`tel:${filtroClienteResumen.telefono.replace(/\s/g, '')}`}
                    className="inline-flex items-center justify-center rounded-md text-xs h-7 px-3 border border-input bg-background hover:bg-accent transition-colors"
                    data-testid="btn-llamar-cliente"
                    title={`Llamar a ${filtroClienteResumen.telefono}`}
                  >
                    <Phone className="w-3.5 h-3.5 mr-1" /> Llamar
                  </a>
                )}
                <Button
                  size="sm"
                  variant="default"
                  className="h-7 text-xs"
                  onClick={() => openReporteDialog(filtroClienteResumen.cliente)}
                  data-testid="btn-enviar-estado-cliente"
                >
                  <Mail className="w-3.5 h-3.5 mr-1" /> Enviar Estado de Cuenta
                </Button>
              </div>
            </div>
          </div>
        )}

        {pagadasConError.length > 0 && (
          <div
            className="mb-3 rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 p-3 flex items-start gap-3"
            data-testid="banner-cxc-sync-errors"
          >
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {pagadasConError.length} factura(s) pagada(s) con error de sync a Alegra
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                Están marcadas como pagadas en el ERP pero el pago no se registró en Alegra.
                Revisá las credenciales y usá &quot;Reintentar todo&quot; o el botón 🔄 de cada fila.
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1">
                {pagadasConError.slice(0, 5).map((c) => (
                  <span key={c.id} className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/50 font-mono">
                    {c.folio} <span className="text-amber-700 dark:text-amber-300">· {formatCLP(c.monto)}</span>
                  </span>
                ))}
                {pagadasConError.length > 5 && (
                  <span className="text-[11px] text-amber-700 dark:text-amber-300">+{pagadasConError.length - 5} más…</span>
                )}
              </div>
            </div>
            <div className="flex flex-col gap-1.5 flex-shrink-0">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs border-amber-400 text-amber-800 hover:bg-amber-100 dark:text-amber-200"
                onClick={() => setFiltroEstado('pagada')}
                data-testid="btn-goto-pagadas"
              >
                Ver en tab Pagadas
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white"
                onClick={reintentarTodosErrores}
                data-testid="btn-reintentar-todos-errores"
              >
                <RefreshCw className="w-3 h-3 mr-1" /> Reintentar todo
              </Button>
            </div>
          </div>
        )}
        {loading ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="rounded-md border overflow-hidden max-h-[500px] overflow-y-auto">
            <table className="w-full text-sm" data-testid="table-cxc">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="text-center px-2 py-2 font-medium w-[36px]">
                    <Checkbox
                      checked={
                        filtered.length > 0 &&
                        filtered.filter((c) => c.estado === 'pendiente' && c.alegra_id).every((c) => selectedIds.has(c.id))
                      }
                      onCheckedChange={(v) => (v ? selectAllVisible() : clearSel())}
                      data-testid="chk-select-all-cxc"
                      aria-label="Seleccionar todas"
                    />
                  </th>
                  <th className="text-left px-3 py-2 font-medium w-[100px]">Folio</th>
                  <th className="text-left px-3 py-2 font-medium">Cliente</th>
                  <th className="text-right px-3 py-2 font-medium w-[130px]">Monto</th>
                  <th className="text-center px-3 py-2 font-medium w-[110px]">F. Alegra</th>
                  <th className="text-center px-3 py-2 font-medium w-[140px]">F. Pago Flujo</th>
                  <th className="text-center px-3 py-2 font-medium w-[100px]">Estado</th>
                  <th className="text-right px-3 py-2 font-medium w-[100px]">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const vencido = c.fecha_venc < today && c.estado === 'pendiente';
                  const fechaOriginal = c.fecha_venc_original || c.fecha_venc;
                  const tieneOverride = c.fecha_venc !== fechaOriginal;
                  const canSelect = c.estado === 'pendiente' && !!c.alegra_id;
                  return (
                    <tr key={c.id} className={`border-t hover:bg-muted/30 ${c.estado === 'pagada' ? 'opacity-60' : ''} ${selectedIds.has(c.id) ? 'bg-emerald-50/40 dark:bg-emerald-950/10' : ''}`} data-testid={`row-cxc-${c.id}`}>
                      <td className="px-2 py-1.5 text-center">
                        {canSelect ? (
                          <Checkbox
                            checked={selectedIds.has(c.id)}
                            onCheckedChange={() => toggleSel(c.id)}
                            data-testid={`chk-cxc-${c.id}`}
                            aria-label={`Seleccionar ${c.folio}`}
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-xs">
                        {c.folio}
                        {c.cliente_rut && (
                          <span className="block text-[10px] text-muted-foreground font-normal">{c.cliente_rut}</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 truncate max-w-[250px]" title={c.cliente}>{c.cliente}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums font-medium">{formatCLP(c.monto)}</td>
                      <td className="px-3 py-1.5 text-center text-xs text-muted-foreground tabular-nums">
                        {fechaOriginal || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <FechaPagoPicker
                          fechaOriginal={fechaOriginal}
                          fechaModificada={tieneOverride ? c.fecha_venc : ''}
                          vencida={vencido}
                          onSet={(iso) => changeFechaInline(c, iso)}
                          onClear={() => changeFechaInline(c, fechaOriginal)}
                          labelOriginal="Alegra"
                          labelModificada="Flujo"
                          testId={`fecha-pago-cxc-${c.id}`}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-center">
                        <Badge className={`${estadosColors[c.estado] || ''} text-[10px]`}>{c.estado}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-right">
                        {c.estado === 'pendiente' && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                            onClick={() => marcarPagada(c)}
                            data-testid={`btn-pagar-cxc-${c.id}`}
                            title="Marcar pagada (y registrar en Alegra)"
                          >
                            <BadgeCheck className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {c.estado === 'pagada' && c.alegra_sync_error && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                            onClick={() => reintentarAlegra(c)}
                            data-testid={`btn-retry-alegra-cxc-${c.id}`}
                            title={`Reintentar sync Alegra: ${c.alegra_sync_error}`}
                          >
                            <RefreshCw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(c)} data-testid={`btn-edit-cxc-${c.id}`}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => doDelete(c)} data-testid={`btn-delete-cxc-${c.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-muted-foreground text-sm">
                    No hay facturas {filtroEstado !== 'todos' ? filtroEstado : ''}.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg" data-testid="dialog-cxc">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Nueva CxC' : `Editar ${editing?.folio || ''}`}</DialogTitle>
            <DialogDescription>Factura de venta con fecha de pago editable.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Folio</Label>
                <Input value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} placeholder="FX123" data-testid="input-cxc-folio" />
              </div>
              <div>
                <Label>Monto (CLP)</Label>
                <Input type="number" value={form.monto} onChange={(e) => setForm({ ...form, monto: Number(e.target.value) || 0 })} data-testid="input-cxc-monto" />
              </div>
            </div>
            <div>
              <Label>Cliente</Label>
              <Input value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} placeholder="Nombre del cliente" data-testid="input-cxc-cliente" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Fecha vencimiento</Label>
                <DatePickerInput value={form.fecha_venc} onChange={(iso) => setForm({ ...form, fecha_venc: iso })} data-testid="input-cxc-fecha" />
                {editing !== 'new' && form.fecha_venc_original && form.fecha_venc !== form.fecha_venc_original && (
                  <p className="text-[10px] text-amber-600 mt-1">Original: {form.fecha_venc_original}</p>
                )}
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={form.estado} onValueChange={(v) => setForm({ ...form, estado: v })}>
                  <SelectTrigger data-testid="select-cxc-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendiente">Pendiente</SelectItem>
                    <SelectItem value="mora">En mora</SelectItem>
                    <SelectItem value="pagada">Pagada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Nota</Label>
              <Input value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} placeholder="Observación opcional" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={close}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="btn-save-cxc">
              {saving ? 'Guardando…' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog: confirmación de marcar pagada + push Alegra.
          Se usa un AlertDialog en-app (no window.confirm) porque este panel
          vive dentro de un Dialog Radix y el confirm nativo puede quedar oculto. */}
      <AlertDialog open={!!confirmPay} onOpenChange={(o) => !o && !paying && setConfirmPay(null)}>
        <AlertDialogContent data-testid="alertdialog-pagar-cxc" className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <BadgeCheck className="w-5 h-5 text-emerald-600" />
              ¿Marcar como PAGADA?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {confirmPay?.cxc && (
                  <div className="bg-slate-50 dark:bg-slate-900/40 border rounded p-2.5 space-y-1">
                    <div className="flex justify-between"><span className="text-muted-foreground">Folio:</span><span className="font-mono font-semibold">{confirmPay.cxc.folio}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Cliente:</span><span className="font-medium truncate ml-2 max-w-[220px]" title={confirmPay.cxc.cliente}>{confirmPay.cxc.cliente}</span></div>
                    <div className="flex justify-between"><span className="text-muted-foreground">Monto:</span><span className="font-bold tabular-nums text-emerald-700">{formatCLP(confirmPay.cxc.monto)}</span></div>
                  </div>
                )}
                <ul className="list-disc pl-5 text-[13px] text-muted-foreground space-y-0.5">
                  <li>Se registrará un pago en <strong>Alegra</strong> (Banco de Chile 1).</li>
                  <li>Si la factura ya tuvo pagos parciales vía factoring, solo se pushea el saldo restante.</li>
                  <li>La factura <strong>no volverá a aparecer</strong> en CxC después del próximo sync.</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={paying} data-testid="btn-cancel-pagar-cxc">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); doMarcarPagada(); }}
              disabled={paying}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-confirm-pagar-cxc"
            >
              {paying ? 'Procesando…' : 'Sí, marcar pagada'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal Punto 4: Enviar Estado de Cuenta por cliente */}
      <Dialog open={reporteDialog} onOpenChange={(o) => { if (!o) setReporteDialog(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="dialog-reporte-cxc">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-600" />
              Enviar Estado de Cuenta por cliente
            </DialogTitle>
            <DialogDescription>
              Elige un cliente con facturas pendientes. Se genera un PDF con todas sus CxC (vencidas destacadas en rojo) y se envía por email vía Microsoft Graph.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs">Cliente</Label>
              <SearchableCombobox
                items={clientesConSaldo.map((c) => ({
                  value: c.cliente_id,
                  label: c.cliente_nombre,
                  subLabel: `${c.facturas_count} fact · ${formatCLP(c.saldo_total)}${c.vencidas_count > 0 ? ` · ${c.vencidas_count} vencidas` : ''}`,
                }))}
                value={selClienteId}
                onChange={(item) => {
                  const v = item ? String(item.value) : '';
                  setSelClienteId(v);
                  loadReportePreview(v);
                }}
                placeholder="Buscar cliente por nombre…"
                emptyMessage="Sin clientes con saldo pendiente"
                testid="combobox-cliente-reporte-cxc"
              />
              <p className="text-[11px] text-muted-foreground mt-0.5">
                {clientesConSaldo.length} clientes con saldo pendiente en Alegra.
              </p>
            </div>

            {reporteLoadingPreview && <Skeleton className="h-20 w-full" />}

            {reportePreview && !reporteLoadingPreview && (
              <div className="rounded-md border p-3 bg-slate-50 dark:bg-slate-900/40 space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cliente:</span>
                  <span className="font-medium">{reportePreview.cliente?.nombre}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Facturas:</span>
                  <span className="font-medium">{reportePreview.count}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Saldo total:</span>
                  <span className="font-bold tabular-nums">{formatCLP(reportePreview.total_saldo)}</span>
                </div>
                {reportePreview.vencidas_count > 0 && (
                  <div className="flex justify-between text-red-700">
                    <span className="text-muted-foreground">Vencidas:</span>
                    <span className="font-bold tabular-nums">
                      {reportePreview.vencidas_count} · {formatCLP(reportePreview.total_vencido)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1 border-t">
                  <span className="text-muted-foreground">Email Alegra:</span>
                  <span className={`font-mono text-[11px] ${reportePreview.cliente?.email ? '' : 'text-orange-600'}`}>
                    {reportePreview.cliente?.email || '(sin email en Alegra — ingrésalo en TO)'}
                  </span>
                </div>
              </div>
            )}

            {reportePreview && (
              <>
                <div>
                  <Label className="text-xs">TO (separar emails con coma)</Label>
                  <Input
                    value={reporteTO}
                    onChange={(e) => setReporteTO(e.target.value)}
                    placeholder="cliente@empresa.cl"
                    data-testid="input-reporte-to"
                    className="text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">CC (separar emails con coma)</Label>
                  <Input
                    value={reporteCC}
                    onChange={(e) => setReporteCC(e.target.value)}
                    data-testid="input-reporte-cc"
                    className="text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Asunto</Label>
                  <Input
                    value={reporteSubject}
                    onChange={(e) => setReporteSubject(e.target.value)}
                    data-testid="input-reporte-subject"
                    className="text-xs"
                  />
                </div>
                <div>
                  <Label className="text-xs">Mensaje personal (opcional)</Label>
                  <textarea
                    value={reporteMsg}
                    onChange={(e) => setReporteMsg(e.target.value)}
                    placeholder="Ej: Le recordamos amablemente..."
                    data-testid="input-reporte-msg"
                    rows={3}
                    className="w-full text-xs rounded-md border bg-background px-2 py-1.5"
                  />
                </div>
              </>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReporteDialog(false)} data-testid="btn-cancel-reporte">
              Cancelar
            </Button>
            <Button
              variant="outline"
              onClick={downloadReportePdf}
              disabled={!selClienteId || !reportePreview}
              data-testid="btn-preview-pdf-reporte"
            >
              <Download className="w-4 h-4 mr-1" /> Descargar PDF
            </Button>
            <Button
              onClick={enviarReporte}
              disabled={!selClienteId || !reportePreview || reporteSending}
              data-testid="btn-send-reporte"
            >
              <Mail className="w-4 h-4 mr-1" /> {reporteSending ? 'Enviando…' : 'Enviar Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo: Cobro agrupado (batch) — 1 solo Ingreso en Alegra */}
      <Dialog open={showBatchDialog} onOpenChange={(o) => !o && !batchProcessing && setShowBatchDialog(false)}>
        <DialogContent className="max-w-lg" data-testid="dialog-batch-cobro">
          <DialogHeader>
            <DialogTitle>Cobro agrupado — 1 Ingreso en Alegra</DialogTitle>
            <DialogDescription>
              Se creará UN solo pago (Ingreso) en Alegra por{' '}
              <b className="tabular-nums">{formatCLP(selectedTotal)}</b>, aplicado a las{' '}
              <b>{selectedRows.length}</b> factura(s) de <b>{selectedClienteNombre}</b>
              {selectedClienteRut ? ` (${selectedClienteRut})` : ''}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Fecha de pago</Label>
              <Input
                type="date"
                value={batchFecha}
                onChange={(e) => setBatchFecha(e.target.value)}
                className="h-8 text-sm"
                data-testid="input-batch-fecha"
              />
            </div>
            <div>
              <Label className="text-xs">Nota / observaciones (opcional)</Label>
              <Input
                value={batchNota}
                onChange={(e) => setBatchNota(e.target.value)}
                placeholder={`Cobro agrupado — ${selectedRows.length} factura(s)`}
                className="h-8 text-sm"
                data-testid="input-batch-nota"
              />
            </div>
            <div className="rounded border max-h-56 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-2 py-1 font-medium">Folio</th>
                    <th className="text-right px-2 py-1 font-medium">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedRows.map((c) => (
                    <tr key={c.id} className="border-t">
                      <td className="px-2 py-1 font-mono">{c.folio}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{formatCLP(c.monto)}</td>
                    </tr>
                  ))}
                  <tr className="border-t bg-emerald-50 dark:bg-emerald-950/20 font-medium">
                    <td className="px-2 py-1">Total</td>
                    <td className="px-2 py-1 text-right tabular-nums text-emerald-700">{formatCLP(selectedTotal)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBatchDialog(false)}
              disabled={batchProcessing}
              data-testid="btn-batch-cancelar"
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmarBatchCobro}
              disabled={batchProcessing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="btn-batch-confirmar"
            >
              {batchProcessing ? 'Registrando en Alegra…' : `Confirmar (1 Ingreso · ${formatCLP(selectedTotal)})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
